<!-- Modal Overlay -->
<div class="modal-overlay" [class.stacked-overlay]="modalMode === 'preview'" (click)="onClose()">
  <div class="modal-container" (click)="$event.stopPropagation()">
    <!-- Modal Header -->
    <div class="modal-header">
      <div class="header-text">
        <h2 class="modal-title">{{ csvPreviewData ? csvPreviewData.fleetName : fleetName }}</h2>
        <p class="modal-subtitle" *ngIf="!csvPreviewData">
          {{ licensePlates().length }} license plate{{ licensePlates().length === 1 ? '' : 's' }}
          <ng-container *ngIf="locationName"> · {{ locationName }}</ng-container>
        </p>
      </div>
      <button class="close-btn" (click)="onClose()">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M9 3L3 9M3 3L9 9"
            stroke="#555963"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <!-- Import Button -->
    <div class="content-header" *ngIf="!csvPreviewData">
      <button class="select-trigger" *ngIf="!isSelectionMode()" (click)="onEnterSelectionMode()">
        Select
      </button>
      <button class="import-btn" (click)="onImportLicensePlates()">
        <img
          src="assets/images/upload-icon-black.svg"
          alt="Upload"
          class="upload-icon"
          width="16"
          height="16"
        />
        Import license
      </button>
    </div>

    <!-- License Plates Table Container -->
    <div class="license-plates-container">
      <!-- Loading State -->
      <div *ngIf="loading()" class="loading-state">
        <p>Loading license plates...</p>
      </div>

      <!-- Error State -->
      <div *ngIf="error()" class="error-state">
        <p>{{ error() }}</p>
      </div>

      <!-- License Plates Table -->
      <div *ngIf="!loading() && !error()" class="license-plates-table">
        <!-- Table Header -->
        <div class="table-header">
          <div class="header-cell checkbox-cell" *ngIf="isSelectionMode()">
            <input
              type="checkbox"
              class="select-all-checkbox"
              [checked]="
                getSelectedPlatesCount() === licensePlates().length && licensePlates().length > 0
              "
              [indeterminate]="
                getSelectedPlatesCount() > 0 && getSelectedPlatesCount() < licensePlates().length
              "
              (change)="
                getSelectedPlatesCount() === licensePlates().length
                  ? deselectAllPlates()
                  : selectAllPlates()
              "
            />
          </div>
          <div class="header-cell plate-header">
            <span class="header-text">License Plate</span>
          </div>
          <div class="header-cell state-header">
            <span class="header-text">State</span>
          </div>
          <div class="header-cell action-header" *ngIf="modalMode !== 'preview'">
            <span class="header-text">Action</span>
          </div>
        </div>

        <!-- Table Rows -->
        <div class="table-body">
          <div
            *ngFor="let plate of licensePlates(); trackBy: trackByIndex"
            class="table-row"
            [class.selected-row]="isPlateSelected(plate.id)"
          >
            <!-- Checkbox Cell (only in selection mode) -->
            <div class="checkbox-cell" *ngIf="isSelectionMode()">
              <input
                type="checkbox"
                class="plate-checkbox"
                [checked]="isPlateSelected(plate.id)"
                (change)="togglePlateSelection(plate.id)"
              />
            </div>

            <!-- Plate Cell -->
            <div class="plate-cell" [class.editing-cell]="editingPlateId() === plate.id">
              <!-- Preview Mode: Just display license plate -->
              <span *ngIf="modalMode === 'preview'" class="license-plate">
                {{ plate.license_plate }}
              </span>

              <!-- Edit Mode: Input field with Save/Cancel buttons -->
              <div *ngIf="editingPlateId() === plate.id" class="edit-mode">
                <input
                  type="text"
                  class="edit-input"
                  [value]="editingValue()"
                  (input)="onInputChange($event)"
                  (keyup.enter)="saveEdit()"
                  (keyup.escape)="cancelEdit()"
                  [disabled]="updatingPlate()"
                  aria-label="License plate"
                  #editInput
                />
                <mat-select
                  class="form-select edit-state-select"
                  aria-label="State"
                  placeholder="State"
                  [value]="editingState()"
                  (selectionChange)="editingState.set($event.value)"
                  [disabled]="updatingPlate()"
                >
                  <mat-option value="">No state</mat-option>
                  <mat-option *ngFor="let state of states" [value]="state.code">
                    {{ state.code }} – {{ state.name }}
                  </mat-option>
                </mat-select>
                <button class="cancel-btn" (click)="cancelEdit()" [disabled]="updatingPlate()">
                  Cancel
                </button>
                <button
                  class="save-btn"
                  (click)="saveEdit()"
                  [disabled]="updatingPlate() || !editingValue().trim()"
                >
                  <span *ngIf="!updatingPlate()">Save</span>
                  <span *ngIf="updatingPlate()">Saving...</span>
                </button>
                <!-- Error message for update -->
                <div *ngIf="updateError()" class="update-error">
                  {{ updateError() }}
                </div>
              </div>

              <!-- View Mode: plain license plate text -->
              <span
                *ngIf="editingPlateId() !== plate.id && modalMode !== 'preview'"
                class="license-plate"
              >
                {{ plate.license_plate }}
              </span>
            </div>

            <!-- State Cell (the inline editor above carries the state input while editing) -->
            <div class="state-cell" *ngIf="editingPlateId() !== plate.id">
              <span class="license-plate" [class.no-state]="!plate.state">
                {{ plate.state || '—' }}
              </span>
            </div>

            <!-- Action Cell: Edit/Delete links -->
            <div
              class="action-cell"
              *ngIf="modalMode !== 'preview' && editingPlateId() !== plate.id && !isSelectionMode()"
            >
              <button class="link-edit" (click)="onEdit(plate.id)">Edit</button>
              <button class="link-delete" (click)="onDelete(plate.id)">Delete</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add License Plate Section -->
    <div class="add-plate-section" *ngIf="!csvPreviewData && !isSelectionMode()">
      <label for="add-plate-input" class="add-plate-label">Add license plate</label>
      <div class="add-plate-row">
        <input
          id="add-plate-input"
          type="text"
          class="add-plate-input"
          placeholder="1X25FG4-CA, 8FGH227"
          [value]="newPlatesInput()"
          (input)="onAddPlatesInputChange($event)"
          (keyup.enter)="onAddPlates()"
          [disabled]="addingPlates()"
        />
        <button
          class="add-plate-button"
          (click)="onAddPlates()"
          [disabled]="addingPlates() || !newPlatesInput().trim()"
        >
          <span *ngIf="!addingPlates()">Add</span>
          <span *ngIf="addingPlates()">Adding...</span>
        </button>
      </div>
      <div class="add-plate-hint">
        Add the state after a hyphen, e.g. 1X25FG4-CA. If left out, it's filled from the fleet's
        location when all its locations are in one state.
      </div>
      <div *ngIf="addPlatesError()" class="add-plate-error">
        {{ addPlatesError() }}
      </div>
    </div>

    <!-- Footer Actions (when in selection mode) -->
    <div *ngIf="isSelectionMode()" class="modal-footer">
      <button class="cancel-btn" (click)="exitSelectionMode()" [disabled]="deletingPlates()">
        Cancel
      </button>
      <button
        class="delete-btn"
        (click)="onBulkAction('delete')"
        [disabled]="getSelectedPlatesCount() === 0 || deletingPlates()"
      >
        <span *ngIf="!deletingPlates()">Delete ({{ getSelectedPlatesCount() }})</span>
        <span *ngIf="deletingPlates()">Deleting...</span>
      </button>
      <!-- Delete error message -->
      <div *ngIf="deleteError()" class="delete-error">
        {{ deleteError() }}
      </div>
    </div>
  </div>
</div>

<!-- Confirmation Modal -->
<app-confirmation-modal
  [isVisible]="showConfirmationModal()"
  [data]="confirmationModalData()"
  (cancel)="onConfirmationCancel()"
  (confirm)="onConfirmationConfirm()"
>
</app-confirmation-modal>
